import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { MoovyButton, UserRoleIcon } from '../components'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import MenuIcon from '@mui/icons-material/Menu'
import Grid from '@mui/material/Grid2'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import useAuth from '../hooks/useAuth'
import useLocale from '../hooks/useLocale'
import localizationResources from '../i18nResources'
import CarLogo from '../resources/carLogo.svg'
import { appRoutes } from './permissions'

interface UserItemProps {
  anchorElement: HTMLElement | null
  onOpenUserMenu: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onCloseMenu: () => void
  onLogout: () => void
}
const UserItem: React.FC<UserItemProps> = ({
  anchorElement,
  onOpenUserMenu,
  onCloseMenu,
  onLogout,
}) => {
  const navigate = useNavigate()
  const intl = useIntl()
  const { locale, setLocale } = useLocale()
  const { isAuthenticated, isAdmin, userFullName, usernameForAvatar } =
    useAuth()

  const locOptions = localizationResources.supportedLanguages.map((key) => {
    return {
      key: key,
      text: intl.formatMessage({ id: `menu.user.${key}` }),
    }
  })
  return (
    <React.Fragment>
      <Avatar
        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
          onOpenUserMenu(event)
        }
      >
        {usernameForAvatar}
      </Avatar>
      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={onCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {isAuthenticated && (
          <MenuItem onClick={() => navigate(appRoutes.USER_DETAILS)}>
            <Box display="flex" gap="5px">
              <UserRoleIcon />
              {userFullName}
            </Box>
          </MenuItem>
        )}
        {isAuthenticated && <Divider />}
        {locOptions.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => {
              setLocale(item.key)
              onCloseMenu()
            }}
            disabled={item.key === locale}
          >
            {item.text}
          </MenuItem>
        ))}
        {isAuthenticated && <Divider />}
        {isAuthenticated && (
          <MenuItem
            onClick={() => {
              onLogout()
              onCloseMenu()
            }}
          >
            <Box display="flex" gap="5px">
              <LogoutRoundedIcon />
              <FormattedMessage id="menu.user.logout" />
            </Box>
          </MenuItem>
        )}
      </Menu>
    </React.Fragment>
  )
}

interface AppMenuTopProps {
  anchorElUser: HTMLElement | null
  appMenuOpen: boolean
  onOpenUserMenu: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onCloseMenu: () => void
  onLogout: () => void
  onClickHamburgerMenu: () => void
  onClickLogo: () => void
}
const AppMenuTop: React.FC<AppMenuTopProps> = ({
  anchorElUser,
  appMenuOpen,
  onOpenUserMenu,
  onCloseMenu,
  onLogout,
  onClickHamburgerMenu,
  onClickLogo,
}) => {
  const { isAuthenticated } = useAuth()
  const intl = useIntl()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <React.Fragment>
      <Grid container sx={{ flexGrow: 1, alignItems: 'center' }}>
        <Grid
          size={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            height: 30,
            gap: 2,
          }}
        >
          <div
            style={{ display: 'flex', cursor: 'pointer' }}
            onClick={onClickLogo}
          >
            <img src={CarLogo} />
          </div>
          {!isMobile && (
            <div
              style={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={onClickLogo}
            >
              {/* Uncomment to add app name to top menu
              <h2 style={{ textTransform: 'uppercase' }}>
                {MoovyConstantNames.APP_FULLNAME}
              </h2>
            */}
            </div>
          )}
          {isAuthenticated && (
            <>
              {appMenuOpen && isMobile ? (
                <MoovyButton
                  startIcon={<CloseRoundedIcon style={{ color: 'white' }} />}
                  onClick={onClickHamburgerMenu}
                  showAsIconButton
                  aria-label={intl.formatMessage({
                    id: 'appMenuTop.button.closeMenu',
                  })}
                />
              ) : (
                <MoovyButton
                  startIcon={<MenuIcon style={{ color: 'white' }} />}
                  onClick={onClickHamburgerMenu}
                  showAsIconButton
                  aria-label={intl.formatMessage({
                    id: 'appMenuTop.button.menu',
                  })}
                />
              )}
            </>
          )}
        </Grid>
        <Grid
          size={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <UserItem
            anchorElement={anchorElUser}
            onOpenUserMenu={onOpenUserMenu}
            onCloseMenu={onCloseMenu}
            onLogout={onLogout}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default AppMenuTop
